import { useState, useEffect } from "react"
import { Button } from "@/components/ui/button"
import SignupDialog from "@/components/SignupDialog"

export default function ApplicationSection() {
  const [isSignupOpen, setIsSignupOpen] = useState(false)
  const [isLoggedIn, setIsLoggedIn] = useState(false)

  useEffect(() => {
    const checkLoggedStatus = () => {
      const appData = document.getElementById('app-data');
      if (appData) {
        const logged = appData.getAttribute('logged');
        setIsLoggedIn(!!logged)
      } else {
        setIsLoggedIn(false)
      }
    }

    checkLoggedStatus()

    const observer = new MutationObserver(checkLoggedStatus)
    observer.observe(document.body, { childList: true, subtree: true })

    return () => {
      observer.disconnect()
    }
  }, [])

  const handleWebButtonClick = (e: React.MouseEvent) => {
    if (!isLoggedIn) {
      e.preventDefault()
      setIsSignupOpen(true)
    }
  }

  return (
    <>
      <section id="application" className="py-16 bg-gradient-to-br from-background to-accent/5">
      <div className="container mx-auto px-6 lg:px-12 space-y-16">
        
        {/* Section Header */}
        <div className="text-center space-y-6">
          <h2 className="text-3xl md:text-5xl font-display font-bold leading-tight">
            Créez partout, <span className="text-gradient">synchronisez tout</span>
          </h2>
          <p className="text-lg text-muted-foreground leading-relaxed max-w-2xl mx-auto">
            Que vous préfériez votre ordinateur ou votre smartphone, nous avons la solution parfaite pour créer vos albums photo.
          </p>
        </div>

        {/* Applications Grid - Reorganized Layout */}
        <div className="grid lg:grid-cols-2 gap-16 items-center max-w-7xl mx-auto relative">
          {/* Séparateur vertical */}
          <div className="hidden lg:block absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 w-px h-64 bg-gradient-to-b from-transparent via-primary/30 to-transparent"></div>
          
          {/* Application Mobile */}
          <div className="space-y-8">
            <div className="space-y-4 text-center">
              <h3 className="text-2xl font-display font-bold leading-tight">
                Application Mobile
              </h3>
              <p className="text-muted-foreground leading-relaxed">
                Créez vos albums photo directement depuis votre smartphone avec 
                notre application intuitive et synchronisation cloud.
              </p>
            </div>

            <div className="flex flex-col lg:flex-row gap-8 items-center">
              {/* Mobile App Visual - Left */}
              <div className="relative flex justify-center lg:justify-start flex-shrink-0">
                <div className="relative">
                  <img 
                    src="/images/home/app.jpg"
                    alt="Application mobile Wememberz pour créer des albums photo"
                    className="relative w-48 h-auto animate-float rounded-3xl"
                  />
                </div>
              </div>

              {/* Mobile Features & Downloads - Right */}
              <div className="flex-1 space-y-6">
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <span className="text-lg">📱</span>
                    </div>
                    <div>
                      <h4 className="font-semibold mb-1">Import facile</h4>
                      <p className="text-muted-foreground text-sm">
                        Importez vos photos depuis votre galerie ou cloud en un clic.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-3">
                    <div className="w-10 h-10 bg-secondary/20 rounded-lg flex items-center justify-center flex-shrink-0">
                      <span className="text-lg">☁️</span>
                    </div>
                    <div>
                      <h4 className="font-semibold mb-1">Synchronisation</h4>
                      <p className="text-muted-foreground text-sm">
                        Synchronisation automatique entre tous vos appareils.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Download Buttons */}
                <div className="flex gap-3 items-center justify-center lg:justify-start">
                  <a 
                    href="https://apps.apple.com/us/app/wememberz/id6444099634"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <img 
                      src="/images/home/btn_app_apple.png"
                      alt="Télécharger sur l'App Store"
                      className="h-9 cursor-pointer hover:scale-105 transition-transform duration-200"
                    />
                  </a>
                  <a 
                    href="https://play.google.com/store/apps/details?id=com.wememberz"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <img 
                      src="/images/home/btn_app_google.png"
                      alt="Télécharger sur Google Play"
                      className="h-9 cursor-pointer hover:scale-105 transition-transform duration-200"
                    />
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Plateforme Web */}
          <div className="space-y-8">
            <div className="space-y-4 text-center">
              <h3 className="text-2xl font-display font-bold leading-tight">
                Plateforme Web
              </h3>
              <p className="text-muted-foreground leading-relaxed">
                Profitez d'un grand écran pour une création plus précise avec 
                tous les outils professionnels.
              </p>
            </div>

            <div className="flex flex-col lg:flex-row gap-8 items-center">
              {/* Web Features & CTA - Left */}
              <div className="flex-1 space-y-6 lg:order-1">
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <span className="text-lg">🖥️</span>
                    </div>
                    <div>
                      <h4 className="font-semibold mb-1">Éditeur avancé</h4>
                      <p className="text-muted-foreground text-sm">
                        Outils professionnels et aperçu en temps réel.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-3">
                    <div className="w-10 h-10 bg-secondary/20 rounded-lg flex items-center justify-center flex-shrink-0">
                      <span className="text-lg">⚡</span>
                    </div>
                    <div>
                      <h4 className="font-semibold mb-1">Performance optimisée</h4>
                      <p className="text-muted-foreground text-sm">
                        Traitement ultra-rapide d'images haute résolution.
                      </p>
                    </div>
                  </div>
                </div>

                {/* CTA Button */}
                <Button variant="rainbow" size="lg" className="flex items-center gap-3" asChild>
                  <a href="#albums" onClick={handleWebButtonClick}>
                    <span className="text-lg">🚀</span>
                    Commencer sur le web
                  </a>
                </Button>
              </div>

              {/* Web Platform Visual - Right */}
              <div className="relative flex justify-center lg:justify-end flex-shrink-0 lg:order-2">
                <div className="relative">
                  <img 
                    src="/images/home/desktop.jpg" 
                    alt="Éditeur web Wememberz pour créer des albums photo"
                    className="relative w-48 h-auto animate-float rounded-3xl"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      </section>

      <SignupDialog 
        open={isSignupOpen} 
        onOpenChange={setIsSignupOpen} 
      />
    </>
  )
}